import { ArrowUpRight, Clock3, MapPin, Phone } from "lucide-react";
import { ChapterTag, FadeUp } from "@/components/Reveal";

const OUTLETS = [
  {
    n: "01",
    name: "Bariatu — Flagship",
    address: "Beside Life Care Hospital, Near Booty More, Bariatu Road, Ranchi 834009",
    hours: "11 AM – 10 PM",
    phone: "+919661968613",
    cuisines: ["North Indian", "Tandoor", "Chinese", "Continental"],
    testid: "outlet-card-bariatu",
  },
  {
    n: "02",
    name: "Littleroof by Tasteride",
    address: "Rooftop, Beside Life Care Hospital, Bariatu Road, Ranchi 834009",
    hours: "11 AM – 10 PM",
    phone: "+919661968613",
    cuisines: ["Rooftop Dining", "Multi-Cuisine", "Family"],
    testid: "outlet-card-littleroof",
  },
  {
    n: "03",
    name: "Kanke",
    address: "Opp. SS Memorial College, Jawahar Nagar, Hatma, Kanke, Ranchi 834008",
    hours: "11 AM – 11 PM",
    phone: "+917280003248",
    cuisines: ["Indian", "Chinese", "Tandoor"],
    testid: "outlet-card-kanke",
  },
  {
    n: "04",
    name: "Jail More — Park Food Court",
    address: "Food Court, Bhagwan Birsa Munda Memorial Park, Jail More, Ranchi 834001",
    hours: "10 AM – 7 PM",
    phone: "+9165118262012",
    cuisines: ["Chinese", "Continental", "Quick Bites"],
    testid: "outlet-card-jailmore",
  },
];

export default function RestaurantChapter() {
  return (
    <section id="models" data-testid="restaurant-chapter" className="relative overflow-hidden bg-obsidian px-5 py-24 sm:px-8 lg:py-32">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 50% 40% at 85% 20%, rgba(224,90,43,0.14) 0%, rgba(11,12,16,0) 70%)" }}
      />
      <div className="relative mx-auto max-w-7xl">
        <ChapterTag index="01" label="Dine-In Restaurants" />

        <div className="mt-12 grid grid-cols-1 items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <FadeUp>
              <h2 className="font-display text-2xl font-semibold leading-[1.12] tracking-tight text-cream sm:text-3xl lg:text-4xl" data-testid="restaurant-headline">
                Flagship tables,{" "}
                <span className="font-serif italic font-medium text-saffron">full every night.</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.12}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-400">
                Four dine-in outlets across Ranchi's busiest corridors — hospital hubs, university
                belts and the city's favourite park. North Indian, Tandoor, Chinese and Continental
                kitchens that built our name plate by plate since 2010.
              </p>
            </FadeUp>
            <FadeUp delay={0.2} className="mt-8 flex flex-wrap gap-3">
              {["Multi-Cuisine", "Family Dining", "Rooftop Ambiance", "Prime Locations"].map((t) => (
                <span key={t} className="rounded-full border border-white/12 bg-white/5 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300">
                  {t}
                </span>
              ))}
            </FadeUp>
          </div>

          <div className="relative lg:col-span-6">
            <FadeUp delay={0.15}>
              <div className="relative">
                <div className="overflow-hidden rounded-t-[999px] rounded-b-[2rem] border border-white/10">
                  <img src="/images/north.jpg" alt="North Indian curry and rice at Taste Ride" className="aspect-[4/3.4] w-full object-cover" data-testid="restaurant-image-main" />
                </div>
                <div className="glass-chip absolute -bottom-8 -left-4 w-44 overflow-hidden rounded-2xl p-1.5 sm:-left-10 sm:w-56">
                  <img src="/images/paneer.jpg" alt="Kadai paneer close-up" className="aspect-[4/3] w-full rounded-xl object-cover" data-testid="restaurant-image-overlay" />
                </div>
                <p className="ml-36 mt-12 text-right font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 sm:ml-56 sm:mt-10">
                  North Indian · Tandoor · Chinese · Continental
                </p>
              </div>
            </FadeUp>
          </div>
        </div>

        <div id="outlets" className="mt-24 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4" data-testid="outlets-grid">
          {OUTLETS.map((o, i) => (
            <FadeUp key={o.testid} delay={i * 0.1}>
              <div
                data-testid={o.testid}
                className="group flex h-full flex-col rounded-3xl border border-white/10 bg-coal p-7 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-2 hover:border-flame/50 hover:shadow-[0_24px_60px_rgba(224,90,43,0.18)]"
              >
                <span className="font-mono text-xs font-semibold tracking-[0.3em] text-flame">OUTLET {o.n}</span>
                <h3 className="mt-3 font-display text-xl font-semibold tracking-tight text-cream">{o.name}</h3>
                <p className="mt-4 flex items-start gap-2 text-sm leading-relaxed text-slate-400">
                  <MapPin size={15} className="mt-0.5 shrink-0 text-saffron" />
                  {o.address}
                </p>
                <p className="mt-2 flex items-center gap-2 text-sm text-slate-400">
                  <Clock3 size={15} className="shrink-0 text-saffron" />
                  {o.hours}
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {o.cuisines.map((c) => (
                    <span key={c} className="rounded-full bg-white/5 px-2.5 py-1 text-[11px] text-slate-300">
                      {c}
                    </span>
                  ))}
                </div>
                <div className="mt-auto flex items-center gap-3 pt-6">
                  <a
                    data-testid={`${o.testid}-call`}
                    href={`tel:${o.phone}`}
                    className="flex flex-1 items-center justify-center gap-2 rounded-full border border-white/15 py-2.5 font-display text-sm font-medium text-cream transition-colors hover:border-wa hover:bg-wa/10"
                  >
                    <Phone size={14} /> Call
                  </a>
                  <a
                    data-testid={`${o.testid}-directions`}
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("Taste Ride " + o.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-white/5 py-2.5 font-display text-sm font-medium text-cream transition-colors hover:bg-saffron hover:text-obsidian"
                  >
                    Directions <ArrowUpRight size={14} />
                  </a>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
